<template>
    <div :class="itemClass" :title="view.title" @click="itemClick">
        <ai-tab-home-svg v-if="view.src" class="ai-tab-bar-item-img" />
        <div class="ai-tab-bar-item-title">
            {{ title }}
        </div>
        <CloseButton v-if="!view.buttonCloseHide" class="ai-tab-bar-item-close" @click.stop="closeItemClick" />
    </div>
</template>

<script>
export default {
    name: 'tab-bar-item',
};
</script>

<script setup>
import CloseButton from '@/components/close-button.vue';
import { computed, ref, watch } from 'vue';
import {
    childViewId,
    getHomeView,
    getOpenedTabs,
    resetTabState,
    saveTabContent,
} from '@/layouts/main-layout/components/navigation-bars-data';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { setSaveUserDataPopupVisible } from '../../main-layout-data';
import { processTab } from '../top-menu/utils';
import { deleteUserDataChanges } from '@/service/userdata-service';

const isDirty = ref(false);
const props = defineProps({
    view: {
        type: Object,
        default: () => ({
            src: '/infoenergo/assets/home.svg',
            buttonCloseHide: false,
            name: 'home-view',
        }),
    },
    focusedTab: {
        type: Object,
        default: () => {
            return getHomeView();
        },
    },
    useRoute: {
        type: Boolean,
        default: true,
    },
    focused: {
        type: Boolean,
        default: false,
    },
});

const itemClick = e => {
    emit('itemClick', props.view);
};

watch(
    () => getOpenedTabs().filter(tab => tab.isDirty),
    () => {
        updateIsDirty();
    },
    { deep: true }
);

const updateIsDirty = () => {
    const dirtyTabs = getOpenedTabs().filter(tab => tab.isDirty);
    isDirty.value = dirtyTabs.some(dirtyTab => dirtyTab.title === props.view.title);
};

updateIsDirty();

async function closeItemClick(e) {
    const uiRoute = window.location.pathname;
    const dirtyTabs = getOpenedTabs().filter(tab => tab.isDirty);
    const currDirtyTab = dirtyTabs.find(tab => tab.viewId === props.view.viewId);
    const viewId = childViewId ? childViewId : currDirtyTab?.viewId;

    if (currDirtyTab) {
        const result = await AiCustomConfirmDialog(
            'В закрывающейся вкладке есть несохраненные изменения. Сохранить изменения?'
        );

        let resultInsideCase;

        switch (result) {
            case 1:
                setSaveUserDataPopupVisible(true);
                resultInsideCase = await processTab(currDirtyTab, 0, [currDirtyTab], saveTabContent, true);
                setSaveUserDataPopupVisible(false);
                if (resultInsideCase) {
                    deleteUserDataChanges({ viewId, uiRoute });
                    resetTabState();
                    emit('closeItemClick', props.view);
                }
                break;
            case -1:
                break;
            case 0:
                resetTabState();

                emit('closeItemClick', props.view);
                deleteUserDataChanges({ viewId, uiRoute });
        }
    } else {
        emit('closeItemClick', props.view);
        resetTabState();
    }
}

const emit = defineEmits(['itemClick', 'closeItemClick']);

const itemClass = computed(() => {
    const isSelected = (props.useRoute && props.focusedTab.routePath === props.view.routePath) || props.focused;
    const baseClass = 'ai-tab-bar-item';
    const selectedClass = isSelected ? 'ai-tab-bar-item-selected' : '';
    const dirtyClass = isDirty.value ? 'is-dirty' : '';

    return [baseClass, selectedClass, dirtyClass].filter(Boolean).join(' ');
});

const title = computed(() => (props.view?.title ? props.view.title : ''));
</script>

<style lang="scss">
.is-dirty {
    position: relative;
    padding-left: 20px !important;
}

.is-dirty::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    margin-left: 5px;
    border-radius: 50%;
    background-color: #ffcf41;
}

.ai-tab-bar-item-selected.ai-tab-bar-item-title.is-dirty {
    position: relative;
    padding-left: 20px;
}

.ai-tab-bar-item-selected.ai-tab-bar-item-title.is-dirty::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    margin-left: 5px;
    height: 10px;
    border-radius: 50%;
    background-color: #ffcf41;
}

.ai-tab-bar-item.ai-tab-bar-item-title.is-dirty {
    position: relative;
    padding-left: 20px;
}

.ai-tab-bar-item.ai-tab-bar-item-title.is-dirty::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    margin-left: 5px;
    border-radius: 50%;
    background-color: #ffcf41;
}

.ai-tab-bar-item {
    display: flex;
    flex-direction: row;
    justify-content: left;
    padding: 3px 3px 3px 12px;
    height: 27px;
    margin-left: 2px;
    margin-top: 5px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background-color: #f7f7f7;

    .ai-tab-bar-item-img {
        width: 24px;
        max-width: 24px;
        stroke: #fff;
        padding-bottom: 2px;
    }

    .ai-tab-bar-item-title {
        display: flex;
        align-items: center;
        padding-right: 6px;
        word-wrap: normal;
        white-space: nowrap;
        cursor: default;

        max-width: 250px;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 14px;

        //-webkit-line-clamp: 1;
        //-webkit-box-orient: vertical;
        //display: -webkit-box;
        //white-space: normal;
    }

    .ai-tab-bar-item-close {
        width: 24px;
        height: 24px;
        display: flex;
        flex-direction: row;
        margin-bottom: 1px;
        .ai-close-button-svg {
            stroke: #000;
        }
    }
}

.ai-tab-bar-item-selected {
    @extend .ai-tab-bar-item;
    background-color: #286eb6;
    color: #fff;
    height: 27px;
    margin-top: 5px;
    .ai-tab-bar-item-close {
        .ai-close-button-svg {
            stroke: #fff;
        }
    }
}

.ai-tab-bar-item:hover {
    background-color: #ededed;
    color: #286eb6;
    .ai-tab-bar-item-close {
        .ai-close-button-svg {
            stroke: #000;
        }
    }
    .ai-tab-bar-item-close:hover {
        .ai-close-button-svg {
            stroke: red;
        }
    }
}
</style>
